import moment from 'moment-timezone';
import {
    CalendarDisplayEvent,
    CalendarDot,
    CalendarTypeFilter,
    CalendarViewMode,
    DowColorMapping,
    DowEntry,
    RawCalendarEvent,
    SelectedCalendarEvent,
    ServiceDowColorMapping,
    WellnessCategory,
    WellnessFilterChip,
} from '../../types/Calendar';

export const resolveCommentsEnabled = (event: any): boolean => {
    if (typeof event?.commentsEnabled === 'boolean') return event.commentsEnabled;
    if (typeof event?.CommentsEnabled === 'boolean') return event.CommentsEnabled;
    if (typeof event?.defaults?.commentsEnabled === 'boolean') return event.defaults.commentsEnabled;
    if (typeof event?.defaults?.CommentsEnabled === 'boolean') return event.defaults.CommentsEnabled;
    return false;
};

// filter for each DOW + all categories
export function buildWellnessFilterChips(dowColorMapping: DowColorMapping): WellnessFilterChip[] {
    return [
        { id: 'all', label: 'All' },
        ...Object.entries(dowColorMapping).map(([category, color]) => ({
            id: category,
            label: category,
            color,
        })),
    ];
}

export const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

export const MONTHS_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const WEEKDAYS = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

/** Calendar layout: month grid vs week/day time grid. */
export type CalendarLayoutMode = 'month' | 'week' | 'day';

/** Sunday-start week containing `date` (local calendar date, time zeroed). */
export function getWeekStart(date: Date): Date {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() - day.getDay());
    return day;
}

export function addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

export function getDateKey(year: number, month: number, day: number): string {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function eventMatchesWellnessFilter(
    dows: DowEntry[],
    activeFilter: CalendarTypeFilter,
): boolean {
    if (activeFilter === 'all') return true;
    const selectedCategory = activeFilter.toLowerCase();
    return dows.some((dow) => dow.id.toLowerCase() === selectedCategory);
}

/** View-all / registrations scope + title search. */
export function eventMatchesViewAndSearch(
    event: Pick<CalendarDisplayEvent, 'title' | 'rsvp'>,
    viewMode: CalendarViewMode,
    searchQuery: string,
): boolean {
    if (viewMode === 'registrations' && !event.rsvp) return false;
    const query = searchQuery.trim().toLowerCase();
    if (query && !event.title.toLowerCase().includes(query)) return false;
    return true;
}

/** True when the resident is on the waiting list (not a confirmed registration). */
export function isWaitlisted(status?: string): boolean {
    return status === 'waiting';
}

export function isRegisteredOrWaiting(status?: string): boolean {
    return !!status && ['intend_attend', 'attended', 'waiting'].includes(status);
}

/** Any status the backend sends that isn't registered, waiting, or "not attending" means the resident was declined. */
export function isDeclined(status?: string): boolean {
    return !!status && !['notattending', 'intend_attend', 'attended', 'waiting'].includes(status);
}

export function dowsFromColorMapping(
    colorMapping: ServiceDowColorMapping | null | undefined,
    dominantDow: WellnessCategory | null = null,
    dominantColor: string | null = null,
): DowEntry[] {
    if (colorMapping && Object.keys(colorMapping).length > 0) {
        return Object.entries(colorMapping)
            .filter((entry): entry is [WellnessCategory, string] => !!entry[1])
            .map(([id, color]) => ({ id, color }));
    }
    if (dominantDow && dominantColor) {
        return [{ id: dominantDow, color: dominantColor }];
    }
    return [];
}

export function toDisplayEvents(
    events: RawCalendarEvent[],
    facilityTimezone: string,
    buildSelectedInfo: (event: RawCalendarEvent, start: moment.Moment) => SelectedCalendarEvent,
): CalendarDisplayEvent[] {
    return events
        .map((event, index) => {
            const start = moment.tz(event.start, facilityTimezone);
            if (!start.isValid()) return null;

            const end = event.end
                ? moment.tz(event.end, facilityTimezone)
                : start.clone().add(60, 'minutes');
            const rawDurationMinutes = end.isValid() ? end.diff(start, 'minutes') : 60;
            // Clamp so a bad/missing end (multi-day span, end before start) can't render one
            // event large enough to dominate the week/day time grid.
            const durationMinutes = Math.min(24 * 60, Math.max(15, rawDurationMinutes));
            const isClosed = !!event.isRegistrationClosed && !isRegisteredOrWaiting(event.registrationStatus);
            const declined = isDeclined(event.registrationStatus);

            // Source index disambiguates events that share a type, title, and start
            return {
                id: `${event.typeOfRequest}-${event.attendingId || event.title}-${event.start}-${index}`,
                dateKey: getDateKey(start.year(), start.month(), start.date()),
                time: start.format('h:mm'),
                ampm: start.format('A'),
                title: event.title,
                location: event.location || '—',
                dominantDow: event.dominantDow,
                dominantColor: event.dominantColor,
                dows: event.dows,
                rsvp: isRegisteredOrWaiting(event.registrationStatus),
                isWaitlisted: isWaitlisted(event.registrationStatus),
                // A resident who already holds a registration/waitlist spot isn't shown as closed.
                isRegistrationClosed: isClosed,
                isDeclined: declined,
                // Registration is on, the resident isn't in or declined, and the event isn't full/closed.
                canRegister:
                    !!event.showAttendance &&
                    event.typeOfRequest !== 'resident' &&
                    !isClosed &&
                    !declined &&
                    !isRegisteredOrWaiting(event.registrationStatus),
                selectedEventInfo: buildSelectedInfo(event, start),
                sortKey: start.valueOf(),
                startHourDecimal: start.hour() + start.minute() / 60,
                durationMinutes,
            } as CalendarDisplayEvent;
        })
        .filter((event): event is CalendarDisplayEvent => event !== null)
        .sort((a, b) => a.sortKey - b.sortKey);
}

/**
 * One dot per event on a date, in agenda order.
 * Each dot uses that event's primary (dominant) color. Events without one
 * render as uncategorized so the dot count still matches the event count.
 */
export function getDotsForDate(
    eventsByDate: Map<string, CalendarDisplayEvent[]>,
    dateKey: string,
): CalendarDot[] {
    const events = eventsByDate.get(dateKey) || [];
    return events.map((event) => ({
        id: event.id,
        color: event.dominantColor,
    }));
}

/** Sort an event's DOW tags to match facility wellness order. */
export function sortDowsByMapping(dows: DowEntry[], dowColorMapping: DowColorMapping): DowEntry[] {
    if (dows.length <= 1) return dows;
    const order = Object.keys(dowColorMapping);
    return [...dows].sort((a, b) => {
        const ai = order.indexOf(a.id);
        const bi = order.indexOf(b.id);
        const aRank = ai === -1 ? Number.MAX_SAFE_INTEGER : ai; // if not in mapping, put at end
        const bRank = bi === -1 ? Number.MAX_SAFE_INTEGER : bi; // if not in mapping, put at end
        return aRank - bRank;
    });
}

/** Shift month by ±1, wrapping year. */
export function shiftMonth(year: number, month: number, delta: -1 | 1): { year: number; month: number } {
    const next = month + delta;
    if (next < 0) return { year: year - 1, month: 11 };
    if (next > 11) return { year: year + 1, month: 0 };
    return { year, month: next };
}
